import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Collapse, Form, Modal, ProgressBar, Spinner } from 'react-bootstrap';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  deleteField,
  doc,
  GeoPoint,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  updateEmail,
} from 'firebase/auth';
import { FaBell, FaCamera, FaCheck, FaChevronDown, FaChevronUp, FaLock, FaUserCircle } from 'react-icons/fa';
import { auth, firestore, storage } from '../views/firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import { deleteAccountAndData } from '../utils/deleteAccountAndData';
import { enableWebNotifications, getWebNotificationPrefs, saveWebNotificationPrefs, webNotificationPermission } from '../services/notificationService';
import {
  emptySocialLinks,
  nicknameWords,
  normalizeP4RUrl,
  PLAYER_SOCIAL_PLATFORMS,
  PLAYER_STRENGTH_OPTIONS,
  profileMissingFields,
  validateOfficialSocialLink,
} from '../utils/playerProfileFields';
import './SettingsModal.css';

const tabs = [
  { id: 'basics', label: 'Player' },
  { id: 'media', label: 'Media' },
  { id: 'account', label: 'Account' },
];

function TogglePill({ active, children, onClick, disabled }) {
  return (
    <button
      type="button"
      className={`settings-pill ${active ? 'active' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

export default function UserProfileEditor({ show, onClose }) {
  const { user, loading } = useAuth();

  const [activeTab, setActiveTab] = useState('basics');
  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [bio, setBio] = useState('');
  const [email, setEmail] = useState('');
  const [fargoRating, setFargoRating] = useState('');
  const [otherRating, setOtherRating] = useState('');
  const [keyStrengths, setKeyStrengths] = useState([]);
  const [socialLinks, setSocialLinks] = useState(emptySocialLinks());
  const [socialErrors, setSocialErrors] = useState({});
  const [onlineStatus, setOnlineStatus] = useState('offline');
  const [coachStatus, setCoachStatus] = useState('off');
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [cueImage, setCueImage] = useState(null);
  const [cuePreviewUrl, setCuePreviewUrl] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [notice, setNotice] = useState(null);
  const [expandAccount, setExpandAccount] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [deleteProgress, setDeleteProgress] = useState({ completed: 0, total: 1, status: '', currentStep: '' });
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteProvider, setDeleteProvider] = useState('password');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [notificationBusy, setNotificationBusy] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState(getWebNotificationPrefs);
  const [notificationPermission, setNotificationPermission] = useState(webNotificationPermission());

  const avatarInputRef = useRef(null);
  const cueInputRef = useRef(null);

  const missing = useMemo(
    () => profileMissingFields({ nickname, bio, profileImageUrl: imagePreviewUrl }),
    [bio, imagePreviewUrl, nickname]
  );
  const profileComplete = missing.length === 0;

  useEffect(() => {
    if (!show || loading) return;
    if (!user) {
      onClose?.();
      return;
    }

    let mounted = true;
    (async () => {
      const snap = await getDoc(doc(firestore, 'userprofiles', user.uid));
      if (!mounted) return;

      if (snap.exists()) {
        const d = snap.data();
        setName(d.name || '');
        setNickname(d.nickname || '');
        setBio(d.bio || '');
        setEmail(d.email || user.email || '');
        setFargoRating(d.r4ting || d.fargoRating || '');
        setOtherRating(d.otherRating || '');
        setKeyStrengths(Array.isArray(d.keyStrengths) ? d.keyStrengths.slice(0, 2) : []);
        setOnlineStatus(d.onlineStatus || 'offline');
        setCoachStatus(d.coachStatus || 'off');
        setImagePreviewUrl(d.profileImageUrl || '');
        setCuePreviewUrl(d.cueImageUrl || '');
        setSocialLinks({
          ...emptySocialLinks(),
          ...PLAYER_SOCIAL_PLATFORMS.reduce((out, platform) => {
            out[platform.field] = d[platform.field] || '';
            return out;
          }, {}),
        });
      } else {
        setEmail(user.email || '');
      }
    })().catch(() => setNotice({ type: 'warning', text: 'Profile could not be loaded.' }));

    return () => {
      mounted = false;
    };
  }, [loading, onClose, show, user]);

  const setTimedNotice = (type, text) => {
    setNotice({ type, text });
    window.setTimeout(() => setNotice(null), 3600);
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setProfileImage(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const handleCueChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCueImage(file);
    setCuePreviewUrl(URL.createObjectURL(file));
  };

  const toggleStrength = (strength) => {
    setKeyStrengths((current) => {
      if (current.includes(strength)) return current.filter((item) => item !== strength);
      return [...current, strength].slice(0, 2);
    });
  };

  const updateSocial = (field, value) => {
    setSocialLinks((current) => ({ ...current, [field]: value }));
    setSocialErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const uploadProfileMedia = async (path, file) => {
    const storageRef = ref(storage, path);
    const snap = await uploadBytes(storageRef, file);
    return getDownloadURL(snap.ref);
  };

  const getOnlineLocation = () => new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve(new GeoPoint(position.coords.latitude, position.coords.longitude)),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
  });

  const saveOrUpdateUserProfile = async () => {
    if (!user) return;

    if (!nickname.trim() || !bio.trim() || !imagePreviewUrl.trim()) {
      setTimedNotice('warning', 'Add a nickname, bio, and profile photo to complete your player card.');
      setActiveTab('basics');
      return;
    }

    const nextSocialErrors = {};
    const normalizedSocial = {};
    PLAYER_SOCIAL_PLATFORMS.forEach((platform) => {
      const raw = socialLinks[platform.field] || '';
      if (!validateOfficialSocialLink(platform, raw)) {
        nextSocialErrors[platform.field] = `Use an official ${platform.label} https link.`;
      }
      normalizedSocial[platform.field] = normalizeP4RUrl(raw);
    });

    if (Object.keys(nextSocialErrors).length) {
      setSocialErrors(nextSocialErrors);
      setActiveTab('media');
      setTimedNotice('warning', 'Check your social links.');
      return;
    }

    setIsUpdating(true);
    setNotice(null);

    try {
      const uid = user.uid;
      let profileImageUrl = imagePreviewUrl;
      let cueImageUrl = cuePreviewUrl;

      if (profileImage) {
        profileImageUrl = await uploadProfileMedia(`usersMedia/${uid}/profileImage.jpg`, profileImage);
      }

      if (cueImage) {
        cueImageUrl = await uploadProfileMedia(`usersMedia/${uid}/cueImage.jpg`, cueImage);
      }

      const location = onlineStatus === 'online' ? await getOnlineLocation() : null;
      const shouldBeOnline = onlineStatus === 'online' && !!location;
      const currentMissing = profileMissingFields({ nickname, bio, profileImageUrl });

      const data = {
        playerId: uid,
        name: name.trim(),
        nickname: nickname.trim(),
        nickname_lowercase: nickname.trim().toLowerCase(),
        nickname_words: nicknameWords(nickname),
        bio: bio.trim(),
        email: email || user.email || '',
        coachStatus,
        onlineStatus: shouldBeOnline ? 'online' : 'offline',
        profileImageUrl,
        profileComplete: currentMissing.length === 0,
        profileCompletionMissing: currentMissing,
        timeUpdated: serverTimestamp(),
        location: shouldBeOnline ? location : deleteField(),
        r4ting: fargoRating.trim() || deleteField(),
        fargoRating: fargoRating.trim() || deleteField(), // legacy web compatibility
        otherRating: otherRating.trim() || deleteField(),
        cueImageUrl: cueImageUrl ? cueImageUrl : deleteField(),
        keyStrengths: keyStrengths.length ? keyStrengths : deleteField(),
      };

      PLAYER_SOCIAL_PLATFORMS.forEach((platform) => {
        data[platform.field] = normalizedSocial[platform.field] || deleteField();
      });

      await setDoc(doc(firestore, 'userprofiles', uid), data, { merge: true });
      setImagePreviewUrl(profileImageUrl);
      setCuePreviewUrl(cueImageUrl || '');
      setProfileImage(null);
      setCueImage(null);
      setOnlineStatus(shouldBeOnline ? 'online' : 'offline');
      setTimedNotice('success', 'Player card saved.');
    } catch (error) {
      console.error(error);
      setTimedNotice('warning', error?.message || 'Profile could not be saved.');
    } finally {
      setIsUpdating(false);
    }
  };

  const confirmSignOut = async () => {
    await signOut(auth);
    onClose?.();
  };

  const confirmDeleteAccount = () => {
    setDeletePassword('');
    setDeleteConfirmText('');
    setDeleteError('');
    setDeleteProgress({ completed: 0, total: 1, status: '', currentStep: '' });
    setShowDeleteModal(true);
  };

  const deleteAccount = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') {
      setDeleteError('Type DELETE to confirm.');
      return;
    }

    setDeleteBusy(true);
    setDeleteError('');

    await deleteAccountAndData({
      password: deletePassword,
      provider: deleteProvider,
      onProgress: (progress) => setDeleteProgress(progress),
      onError: (message) => {
        setDeleteError(message);
        setDeleteBusy(false);
      },
      onComplete: () => {
        setDeleteBusy(false);
        setShowDeleteModal(false);
        onClose?.();
      },
    });
  };

  const handleChangeEmail = async () => {
    try {
      const cred = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, cred);
      await updateEmail(user, newEmail);
      await sendEmailVerification(user);
      setEmail(newEmail);
      setShowChangeEmailModal(false);
      setTimedNotice('success', 'Verification email sent.');
    } catch (err) {
      setTimedNotice('warning', err.message);
    }
  };

  const sendPasswordReset = async () => {
    try {
      await sendPasswordResetEmail(auth, email);
      setTimedNotice('success', `Reset sent to ${email}.`);
    } catch (err) {
      setTimedNotice('warning', err.message);
    }
  };

  const turnOnNotifications = async () => {
    if (!user) return;
    setNotificationBusy(true);
    try {
      await saveWebNotificationPrefs(user.uid, notificationPrefs);
      await enableWebNotifications(user.uid);
      setNotificationPermission(webNotificationPermission());
      setTimedNotice('success', 'Browser notifications are enabled for this device.');
    } catch (err) {
      setTimedNotice('warning', err.message || 'Notifications could not be enabled.');
    } finally {
      setNotificationBusy(false);
    }
  };

  if (loading) return null;

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        size="lg"
        centered
        backdropClassName="settings-backdrop"
        dialogClassName="settings-dialog modal-dialog-scrollable"
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title><span>Profile Editor</span><small className="settings-title-sub">Express your Poolsonality</small></Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <section className="settings-hero-card">
            <Form.Control
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="d-none"
            />

            <button
              type="button"
              className="settings-avatar-button"
              onClick={() => avatarInputRef.current?.click()}
            >
              {imagePreviewUrl ? (
                <img src={imagePreviewUrl} alt="Profile" />
              ) : (
                <FaUserCircle />
              )}
              <span><FaCamera /></span>
            </button>

            <div className="settings-hero-copy">
              <strong>{nickname || 'Player Card'}</strong>
              <p>{profileComplete ? 'Ready' : 'Complete your card'}</p>
              <div className="settings-hero-badges">
                <span className={onlineStatus === 'online' ? 'active' : ''}>Visible</span>
                <span className={coachStatus === 'on' ? 'active' : ''}>Coach</span>
                <span>{keyStrengths.length}/2 strengths</span>
              </div>
            </div>
          </section>

          {notice && <Alert className={`settings-alert ${notice.type}`}>{notice.text}</Alert>}

          <div className="settings-tabs" role="tablist">
            {tabs.map((tab) => (
              <button
                type="button"
                key={tab.id}
                className={activeTab === tab.id ? 'active' : ''}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'basics' && (
            <div className="settings-panel-stack">
              <section className="settings-section-card">
                <div className="settings-form-grid">
                  <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control value={name} onChange={(e) => setName(e.target.value.slice(0, 25))} maxLength={25} />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Nickname *</Form.Label>
                    <Form.Control value={nickname} onChange={(e) => setNickname(e.target.value.slice(0, 25))} maxLength={25} />
                  </Form.Group>
                  <Form.Group className="settings-full-row">
                    <Form.Label>Bio *</Form.Label>
                    <Form.Control as="textarea" rows={4} value={bio} onChange={(e) => setBio(e.target.value.slice(0, 300))} maxLength={300} />
                    <Form.Text>{bio.length}/300</Form.Text>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>R4ting</Form.Label>
                    <Form.Control
                      inputMode="numeric"
                      value={fargoRating}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                        setFargoRating(value);
                      }}
                      placeholder="Example: 520"
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>League / local rating</Form.Label>
                    <Form.Control value={otherRating} onChange={(e) => setOtherRating(e.target.value.slice(0, 12))} placeholder="Example: 6" />
                  </Form.Group>
                </div>
              </section>

              <section className="settings-section-card">
                <div className="settings-section-title">
                  <strong>Strengths</strong>
                  <span>Pick 2</span>
                </div>
                <div className="settings-chip-grid">
                  {PLAYER_STRENGTH_OPTIONS.map((strength) => (
                    <TogglePill
                      key={strength}
                      active={keyStrengths.includes(strength)}
                      onClick={() => toggleStrength(strength)}
                      disabled={!keyStrengths.includes(strength) && keyStrengths.length >= 2}
                    >
                      {strength}
                    </TogglePill>
                  ))}
                </div>
              </section>

              <section className="settings-section-card compact">
                <div className="settings-switch-row">
                  <div>
                    <strong>Visible nearby</strong>
                    <span>Uses approximate location while enabled.</span>
                  </div>
                  <Form.Check
                    type="switch"
                    checked={onlineStatus === 'online'}
                    onChange={(e) => setOnlineStatus(e.target.checked ? 'online' : 'offline')}
                  />
                </div>
                <div className="settings-switch-row">
                  <div>
                    <strong>Coach badge</strong>
                    <span>Show coaching availability on your card.</span>
                  </div>
                  <Form.Check
                    type="switch"
                    checked={coachStatus === 'on'}
                    onChange={(e) => setCoachStatus(e.target.checked ? 'on' : 'off')}
                  />
                </div>
              </section>
            </div>
          )}

          {activeTab === 'media' && (
            <div className="settings-panel-stack">
              <section className="settings-section-card">
                <div className="settings-cue-card">
                  <Form.Control ref={cueInputRef} type="file" accept="image/*" onChange={handleCueChange} className="d-none" />
                  <button type="button" className="settings-cue-preview" onClick={() => cueInputRef.current?.click()}>
                    {cuePreviewUrl ? <img src={cuePreviewUrl} alt="Cue" /> : <FaCamera />}
                  </button>
                  <div>
                    <strong>Cue media</strong>
                    <p>Add your cue, case, or table setup.</p>
                    <button type="button" onClick={() => cueInputRef.current?.click()}>Choose image</button>
                    {cuePreviewUrl && <button type="button" onClick={() => { setCueImage(null); setCuePreviewUrl(''); }}>Remove</button>}
                  </div>
                </div>
              </section>

              <section className="settings-section-card">
                <div className="settings-section-title">
                  <strong>Social links</strong>
                  <span>Optional</span>
                </div>
                <div className="settings-form-grid">
                  {PLAYER_SOCIAL_PLATFORMS.map((platform) => (
                    <Form.Group key={platform.field} className="settings-full-row">
                      <Form.Label>{platform.label}</Form.Label>
                      <Form.Control
                        value={socialLinks[platform.field] || ''}
                        onChange={(e) => updateSocial(platform.field, e.target.value)}
                        placeholder={platform.placeholder}
                        isInvalid={!!socialErrors[platform.field]}
                      />
                      <Form.Control.Feedback type="invalid">{socialErrors[platform.field]}</Form.Control.Feedback>
                    </Form.Group>
                  ))}
                </div>
              </section>
            </div>
          )}

          {activeTab === 'account' && (
            <div className="settings-panel-stack">
              <section className="settings-section-card compact p4r-notification-settings">
                <div className="settings-section-title">
                  <strong><FaBell /> Alerts</strong>
                  <span>{notificationPermission === 'granted' ? 'Browser on' : 'Browser permission needed'}</span>
                </div>
                {[
                  ['messagesEnabled', 'Message alerts', 'Chats, replies, and offers.'],
                  ['tournamentsEnabled', 'Tournament alerts', 'Check-ins, brackets, announcements, and match-ready updates.'],
                  ['teamsEnabled', 'Team alerts', 'Requests, roster changes, and team announcements.'],
                  ['nearbyEnabled', 'Nearby suggestions', 'Allow nearby discovery features to use location when needed.'],
                ].map(([key, title, body]) => (
                  <div className="settings-switch-row" key={key}>
                    <div><strong>{title}</strong><span>{body}</span></div>
                    <Form.Check type="switch" checked={!!notificationPrefs[key]} onChange={(e) => {
                      const next = { ...notificationPrefs, [key]: e.target.checked };
                      setNotificationPrefs(next);
                      if (user) saveWebNotificationPrefs(user.uid, next).catch(() => {});
                    }} />
                  </div>
                ))}
                <div className="settings-switch-row">
                  <div><strong>Browser push</strong><span>{notificationPermission === 'denied' ? 'Blocked in browser site settings.' : 'Register this browser for push notifications.'}</span></div>
                  <Button size="sm" onClick={turnOnNotifications} disabled={notificationBusy || notificationPermission === 'granted'}>
                    {notificationBusy ? 'Enabling…' : notificationPermission === 'granted' ? 'Enabled' : 'Enable'}
                  </Button>
                </div>
              </section>

              <section className="settings-section-card compact">
                <button type="button" className="settings-account-toggle" onClick={() => setExpandAccount((value) => !value)}>
                  <span><FaLock /> Account tools</span>
                  {expandAccount ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                <Collapse in={expandAccount}>
                  <div className="settings-account-body">
                    <div className="settings-account-email">{email || 'No email on file'}</div>
                    <button type="button" onClick={() => setShowChangeEmailModal(true)}>Change email</button>
                    <button type="button" onClick={sendPasswordReset}>Reset password</button>
                    <button type="button" onClick={() => { onClose?.(); window.location.assign('/safety'); }}>Safety center</button>
                    <button type="button" className="danger" onClick={confirmDeleteAccount}>Delete account</button>
                  </div>
                </Collapse>
              </section>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-light" onClick={() => setShowSignOutConfirm(true)}>Sign Out</Button>
          <Button className="settings-save-btn" onClick={saveOrUpdateUserProfile} disabled={isUpdating}>
            {isUpdating ? <><Spinner animation="border" size="sm" /> Saving</> : <><FaCheck /> Save</>}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSignOutConfirm} centered onHide={() => setShowSignOutConfirm(false)} contentClassName="settings-mini-modal">
        <Modal.Header closeButton closeVariant="white"><Modal.Title>Sign out?</Modal.Title></Modal.Header>
        <Modal.Footer>
          <Button variant="outline-light" onClick={() => setShowSignOutConfirm(false)}>Cancel</Button>
          <Button variant="warning" onClick={confirmSignOut}>Sign out</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} centered backdrop={deleteBusy ? 'static' : true} keyboard={!deleteBusy} onHide={() => !deleteBusy && setShowDeleteModal(false)} contentClassName="settings-mini-modal">
        <Modal.Header closeButton={!deleteBusy} closeVariant="white"><Modal.Title>Delete account</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="danger">This is permanent.</Alert>
          <Form.Group className="mb-3">
            <Form.Label>Type DELETE</Form.Label>
            <Form.Control value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} disabled={deleteBusy} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Verification</Form.Label>
            <Form.Select value={deleteProvider} onChange={(e) => setDeleteProvider(e.target.value)} disabled={deleteBusy}>
              <option value="password">Email/password</option>
              <option value="google">Google sign-in</option>
            </Form.Select>
          </Form.Group>
          {deleteProvider === 'password' && (
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} disabled={deleteBusy} />
            </Form.Group>
          )}
          {deleteError && <Alert variant="warning">{deleteError}</Alert>}
          {deleteBusy && <ProgressBar animated now={(deleteProgress.completed / Math.max(deleteProgress.total, 1)) * 100} />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-light" disabled={deleteBusy} onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant="danger" disabled={deleteBusy} onClick={deleteAccount}>{deleteBusy ? 'Deleting…' : 'Delete'}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showChangeEmailModal} centered onHide={() => setShowChangeEmailModal(false)} contentClassName="settings-mini-modal">
        <Modal.Header closeButton closeVariant="white"><Modal.Title>Change email</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>New email</Form.Label>
            <Form.Control type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Current password</Form.Label>
            <Form.Control type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-light" onClick={() => setShowChangeEmailModal(false)}>Cancel</Button>
          <Button variant="warning" onClick={handleChangeEmail}>Update</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
